import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { apiPath } from '../../../config/apiBase';
import SearchableSelect from '../../../components/SearchableSelect';

export const EASYCHIT_PRODUCT_ID = 'PROD-EASYCHIT';
export const EASYCHIT_REPORTERS_DS_ID = 'DS-EC-REPORTERS';

const DS_RESOLVE = apiPath('/api/tickets/data-sources/resolve');

const GROUP_CANDIDATES = ['group', 'group_or_office', 'group_or_office_name'];
const COMPANY_CANDIDATES = ['company', 'company_name'];
const BRANCH_CANDIDATES = ['branch', 'branch_name'];
const REPORTED_BY_CANDIDATES = ['reported_by'];
const CONTACT_CANDIDATES = ['contact_no', 'contact', 'contact_number'];
const EMAIL_CANDIDATES = ['reporter_email', 'email'];

export function resolveFieldKey(fieldDefs, candidates) {
  for (const candidate of candidates) {
    const hit = (fieldDefs || []).find((f) => f.field_key === candidate);
    if (hit) return hit.field_key;
  }
  return null;
}

function fieldLabel(fieldDefs, key, fallback) {
  const hit = (fieldDefs || []).find((f) => f.field_key === key);
  return hit?.label || fallback;
}

function EasychitReporterSection({ fieldDefs, domainFields, productId, onChange }) {
  const groupKey = useMemo(() => resolveFieldKey(fieldDefs, GROUP_CANDIDATES), [fieldDefs]);
  const companyKey = useMemo(() => resolveFieldKey(fieldDefs, COMPANY_CANDIDATES), [fieldDefs]);
  const branchKey = useMemo(() => resolveFieldKey(fieldDefs, BRANCH_CANDIDATES), [fieldDefs]);
  const reportedByKey = useMemo(() => resolveFieldKey(fieldDefs, REPORTED_BY_CANDIDATES), [fieldDefs]);
  const contactKey = useMemo(() => resolveFieldKey(fieldDefs, CONTACT_CANDIDATES), [fieldDefs]);
  const emailKey = useMemo(() => resolveFieldKey(fieldDefs, EMAIL_CANDIDATES), [fieldDefs]);

  const group = groupKey ? domainFields?.[groupKey] : '';
  const company = companyKey ? domainFields?.[companyKey] : '';
  const branch = branchKey ? domainFields?.[branchKey] : '';
  const branchReady = Boolean(group && company && branch);

  const [savedReporters, setSavedReporters] = useState([]);
  const [loadingReporters, setLoadingReporters] = useState(false);
  const [selectedReporterValue, setSelectedReporterValue] = useState(null);

  useEffect(() => {
    if (!branchReady || productId !== EASYCHIT_PRODUCT_ID) {
      setSavedReporters([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingReporters(true);
    const token = localStorage.getItem('token');
    axios.post(DS_RESOLVE, {
      data_source_id: EASYCHIT_REPORTERS_DS_ID,
      params: { group, company, branch },
    }, {
      withCredentials: true,
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => { if (!cancelled) setSavedReporters(res.data || []); })
      .catch(() => { if (!cancelled) setSavedReporters([]); })
      .finally(() => { if (!cancelled) setLoadingReporters(false); });
    return () => { cancelled = true; };
  }, [branchReady, productId, group, company, branch]);

  useEffect(() => {
    setSelectedReporterValue(null);
  }, [group, company, branch]);

  if (!reportedByKey) return null;

  const patchFields = (updates) => {
    onChange({ domain_fields: { ...domainFields, ...updates } });
  };

  const bindReporter = (reporter) => {
    setSelectedReporterValue(reporter.value);
    const updates = { [reportedByKey]: reporter.value || reporter.label };
    if (contactKey) updates[contactKey] = reporter.contact_no || '';
    if (emailKey) updates[emailKey] = reporter.email || '';
    patchFields(updates);
  };

  const handleReportedByChange = (value) => {
    const match = savedReporters.find(
      (r) => (r.value || r.label || '').toLowerCase() === value.trim().toLowerCase(),
    );
    if (match) {
      bindReporter(match);
      return;
    }
    setSelectedReporterValue(null);
    patchFields({ [reportedByKey]: value });
  };

  const handlePickReporter = (value) => {
    const reporter = savedReporters.find((r) => r.value === value);
    if (reporter) bindReporter(reporter);
  };

  const locked = Boolean(selectedReporterValue);
  const reportedByValue = domainFields?.[reportedByKey] || '';
  const listId = `easychit-reporters-${branchKey || 'branch'}`;

  return (
    <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
      <div style={{ gridColumn: '1 / -1' }}>
        <h4 style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: '600', color: '#374151' }}>
          Reported By
        </h4>
        <p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>
          Contacts are loaded from the Easychit Branch Reporters data source for this group, company, and branch.
          Select an existing contact or type a new one — new entries are saved automatically when the ticket is created.
        </p>
      </div>

      {!branchReady && (
        <div className="tk-alert tk-alert-warning" style={{ gridColumn: '1 / -1', marginBottom: 0 }}>
          Select Group, Company, and Branch first to load saved contacts for this branch.
        </div>
      )}

      {branchReady && savedReporters.length > 0 && (
        <div>
          <label className="tk-label">Saved contacts for this branch</label>
          <SearchableSelect
            options={savedReporters.map((r) => ({
              value: r.value,
              label: r.label || r.value,
              description: [r.contact_no, r.email].filter(Boolean).join(' · '),
              searchText: `${r.label || ''} ${r.value || ''} ${r.contact_no || ''} ${r.email || ''}`,
            }))}
            value={selectedReporterValue || ''}
            onChange={(v) => handlePickReporter(v)}
            placeholder="Choose saved contact…"
            searchPlaceholder="Search saved contacts…"
            aria-label="Saved contacts"
          />
        </div>
      )}

      <div style={savedReporters.length > 0 && branchReady ? undefined : { gridColumn: '1 / -1' }}>
        <label className="tk-label">
          {fieldLabel(fieldDefs, reportedByKey, 'Reported By')} *
          {loadingReporters ? ' (loading…)' : ''}
        </label>
        <input
          type="text"
          list={branchReady ? listId : undefined}
          value={reportedByValue}
          onChange={(e) => handleReportedByChange(e.target.value)}
          required
          className="tk-field"
          placeholder={branchReady ? 'Select saved contact or type a new name' : 'Enter reported by name'}
        />
        {branchReady && (
          <datalist id={listId}>
            {savedReporters.map((r) => (
              <option key={r.value} value={r.value || r.label} />
            ))}
          </datalist>
        )}
      </div>

      {contactKey && (
        <div>
          <label className="tk-label">{fieldLabel(fieldDefs, contactKey, 'Contact Number')}</label>
          <input
            type="text"
            value={domainFields?.[contactKey] || ''}
            onChange={(e) => {
              setSelectedReporterValue(null);
              patchFields({ [contactKey]: e.target.value });
            }}
            readOnly={locked}
            className="tk-field"
            style={{ background: locked ? '#F8FAFC' : undefined }}
            placeholder="Contact number"
          />
        </div>
      )}

      {emailKey && (
        <div>
          <label className="tk-label">{fieldLabel(fieldDefs, emailKey, 'Email')}</label>
          <input
            type="email"
            value={domainFields?.[emailKey] || ''}
            onChange={(e) => {
              setSelectedReporterValue(null);
              patchFields({ [emailKey]: e.target.value });
            }}
            readOnly={locked}
            className="tk-field"
            style={{ background: locked ? '#F8FAFC' : undefined }}
            placeholder="Email address"
          />
        </div>
      )}
    </div>
  );
}

export default EasychitReporterSection;
